import { supabase } from "../supabase/SupabaseClient";

/* Los viajes que mira Presidencia.
 *
 * ── SE PIDE EL RESUMEN Y NO LOS VIAJES ──────────────────────────────────────
 *
 * `presidencia_viajes` es una fila por viaje: 4.700 y creciendo.
 * `presidencia_resumen` viene agrupado por día y son 1.659: seis veces menos, y
 * el día alcanza para la semana, el mes y el calendario.
 *
 * ── Y NO SE PIDE ENTERO: SÓLO LAS SEMANAS QUE CAMBIARON ────────────────────
 *
 * Eran tres páginas de mil filas cada vez que se abría la pantalla y cada vez
 * que entraba un viaje. Ahora la base le pone un SELLO a cada semana (un md5
 * de lo que decide sus filas), acá se guardan las semanas con su sello, y se
 * piden las filas sólo de las que no coinciden: un día cualquiera, la semana
 * en curso o ninguna. El detalle está en `traerResumen` y en la migración
 * `presidencia_delta_por_semana`.
 *
 * Lo que NO se hace es dar por quieto lo pasado: en dos semanas se tocaron 527
 * viajes de semanas ya cerradas. Lo guardado vale mientras la base diga que el
 * sello es el mismo, y ni un pedido más.
 *
 * ── LA CUENTA LA HACE LA BASE ───────────────────────────────────────────────
 *
 * Semana ISO, región, cliente normalizado y bitrén vienen resueltos de la
 * vista. `fecha` es la que decide la semana —la de carga, y la de salida si no
 * hay carga— y `salio` dice si el viaje ya arrancó; la fecha de salida real
 * sigue existiendo en la vista con su nombre, que es lo que evita que alguien
 * la confunda con ésta. Si se derivaran acá, el Excel exportado y la pantalla podrían no
 * coincidir — y el que lo note va a ser Gabriel, mirando su propio número.
 *
 * Sin `ver.presidencia` la vista devuelve cero filas y no un error, igual que
 * el resto del hub. La pantalla lo distingue con el permiso que ya tiene.
 */

/* Hasta dónde se trae. HOY, y ya no una fecha clavada.
 *
 * ── POR QUÉ ESTUVO CONGELADO EN 2026-07-31 ──────────────────────────────────
 *
 * La importación de la planilla llegó hasta el 29/07 y desde entonces sólo
 * había carga manual: agosto se veía como el peor mes del año cuando lo que
 * estaba flojo era la carga. Se cortó en julio a pedido de Igna — «que sea de
 * julio para atrás, un histórico»— y era lo correcto CON LAS HERRAMIENTAS DE
 * ENTONCES: la pantalla no sabía decir «este mes está a medio cargar».
 *
 * ── POR QUÉ SE DESCONGELA (Igna, 25/08/2026) ────────────────────────────────
 *
 * Ahora sí sabe decirlo: `cargaDeLaSemana` cuenta los días con carga contra los
 * días que van, y el tablero lo avisa arriba. Esconder el período dejó de ser
 * la única forma de no engañar, y esconderlo tenía su propio costo — el período
 * en curso no se podía mirar, y una fecha clavada en el código envejece sola:
 * en septiembre habría escondido también a agosto, ya completo.
 *
 * SE CALCULA AL LLAMAR Y NO AL IMPORTAR. El hub es una pantalla que alguien
 * deja abierta: una constante de módulo se congela en el día en que se cargó
 * la página, y a la medianoche el tablero dejaría de traer el día de hoy sin
 * que nada lo diga.
 */
export function hastaHoy() {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

/* Dónde se guardan las semanas. El `v1` es de la FORMA de lo guardado: si
   cambian las columnas, se sube y lo viejo queda huérfano en vez de leerse
   mal. (Si cambia una REGLA, lo que se sube es `SELLO_VERSION` en la base.) */
const CLAVE_GUARDADO = "presidencia-resumen-v1";

/* Las columnas de `presidencia_resumen`, en orden. Las filas se guardan como
   listas y no como objetos: pesan la mitad, y `localStorage` tiene 5 MB. */
const COLUMNAS = [
  "fecha",
  "anio",
  "semana",
  "sentido",
  "region",
  "tipo",
  "cliente",
  "reconocido",
  "bitren",
  "tipo_acoplado",
  "salio",
  "viajes",
  "tramo_corto",
];

const VACIO = { sellos: {}, semanas: {} };

function leerGuardado() {
  try {
    const g = JSON.parse(localStorage.getItem(CLAVE_GUARDADO) || "null");
    if (g && typeof g.sellos === "object" && typeof g.semanas === "object") {
      return g;
    }
  } catch (e) {
    // Modo privado, almacenamiento bloqueado o un JSON roto: se pide todo.
  }
  return VACIO;
}

function guardar(g) {
  try {
    localStorage.setItem(CLAVE_GUARDADO, JSON.stringify(g));
  } catch (e) {
    // Lleno o bloqueado. Queda lo anterior, que sigue siendo coherente (sus
    // filas con sus sellos), y la próxima vez se piden las mismas semanas.
  }
}

/* Tira lo guardado. Lo llama el cierre de sesión: en una computadora que usan
   varios, los números de Presidencia no se quedan en el navegador para el que
   entra después. */
export function olvidarResumen() {
  try {
    localStorage.removeItem(CLAVE_GUARDADO);
  } catch (e) {
    // Nada que tirar.
  }
}

async function pedirDelta(sellos) {
  const { data, error } = await supabase.rpc("presidencia_resumen_delta", {
    p_sellos: sellos,
  });
  if (error) throw error;
  return data;
}

/* Junta lo que mandó la base con lo guardado. Devuelve las semanas, o `null`
 * si la base dio por buena una semana que acá no está (lo guardado se rompió
 * a medias): ahí no se adivina, se pide todo.
 *
 * Y si una semana cambió y no vino ni una fila, SE TIRA UN ERROR. Una semana
 * con sello tiene por lo menos un viaje; sin filas, es una respuesta cortada,
 * y guardarla sería un mes flojo que no existe. Es el mismo criterio de
 * `traerTodas`: una pantalla rota se arregla, una que miente no.
 */
function juntar(data, guardado) {
  const cambiadas = new Set(data.cambiadas);
  const nuevas = {};
  for (const f of data.filas) {
    const clave = `${f.anio}-${f.semana}`;
    if (!nuevas[clave]) nuevas[clave] = [];
    nuevas[clave].push(COLUMNAS.map((c) => f[c]));
  }

  const semanas = {};
  for (const clave of Object.keys(data.sellos)) {
    if (cambiadas.has(clave)) {
      if (!nuevas[clave]) {
        throw new Error(
          `Faltan filas: la semana ${clave} cambió y la base no mandó ninguna`,
        );
      }
      semanas[clave] = nuevas[clave];
    } else if (Array.isArray(guardado.semanas[clave])) {
      semanas[clave] = guardado.semanas[clave];
    } else {
      return null;
    }
  }
  return semanas;
}

const partes = (clave) => clave.split("-").map(Number);

export async function traerResumen({ hasta = hastaHoy() } = {}) {
  let guardado = leerGuardado();
  let data = await pedirDelta(guardado.sellos);
  let semanas = data && juntar(data, guardado);

  if (data && !semanas) {
    guardado = VACIO;
    data = await pedirDelta({});
    semanas = data && juntar(data, guardado);
  }

  /* `null` es «no tenés `ver.presidencia`». Antes la vista devolvía cero filas
     y acá se mantiene; lo guardado se tira, porque pudo ser de cuando sí. */
  if (!data) {
    olvidarResumen();
    return [];
  }
  if (!semanas) throw new Error("La base no mandó todas las semanas");

  /* Se guarda SÓLO lo que la base nombró: una semana que se quedó sin viajes
     no está en `sellos` y así se va sola. */
  guardar({ sellos: data.sellos, semanas });

  /* EN ORDEN DE SEMANA, y por número: "2026-9" va antes que "2026-10". Adentro
     de cada semana las filas ya vienen ordenadas, y como las semanas no se
     pisan en fechas, el total queda ordenado por fecha como antes. */
  const filas = [];
  const claves = Object.keys(semanas).sort((a, b) => {
    const [aa, as] = partes(a);
    const [ba, bs] = partes(b);
    return aa - ba || as - bs;
  });
  for (const clave of claves) {
    for (const lista of semanas[clave]) {
      const f = {};
      COLUMNAS.forEach((c, i) => {
        f[c] = lista[i];
      });
      if (!hasta || f.fecha <= hasta) filas.push(f);
    }
  }
  return filas;
}

/* Avisa cuando se toca un viaje, para volver a pedir los datos.
 *
 * ── POR QUÉ NO SE ACTUALIZA LA FILA EN MEMORIA ──────────────────────────────
 *
 * El resto del hub usa `supabaseRealtime` y va parchando la lista fila por
 * fila. Acá no se puede: lo que se muestra no son los viajes sino
 * `presidencia_resumen`, que es una vista con el cliente ya normalizado, la
 * semana ISO y la región resueltos por la base. Un `INSERT` sobre
 * `movimientos` llega crudo —«BGH DEFIBE», sin región, sin semana— y
 * normalizarlo de este lado sería tener la regla escrita en dos lugares.
 *
 * Y sería peor que tenerla duplicada: el día que se agregue un alias a
 * `clientes_alias`, la base agruparía bien y la pantalla mal, hasta que
 * alguien recargue. Se vuelve a preguntar y listo — vuelven sólo las semanas
 * que cambiaron.
 *
 * ── SE ESPERAN DOS SEGUNDOS ─────────────────────────────────────────────────
 *
 * Una importación de planilla mete cientos de filas de una: sin el respiro,
 * serían cientos de consultas, una por fila. Con dos segundos, es
 * una sola cuando termina. Un viaje suelto se ve igual de rápido.
 */
// Con número, como el de Mensajes: dos pantallas (o una montada dos veces)
// escuchando a la vez con el mismo nombre comparten canal y la segunda
// revienta al agregar su `.on()` sobre uno ya suscripto.
let canalesMovimientos = 0;
export function alCambiarMovimientos(callback, esperaMs = 2000) {
  let pendiente = null;
  const canal = supabase
    .channel(`presidencia-movimientos:${(canalesMovimientos += 1)}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "movimientos" },
      () => {
        clearTimeout(pendiente);
        pendiente = setTimeout(callback, esperaMs);
      },
    )
    .subscribe();

  return () => {
    clearTimeout(pendiente);
    supabase.removeChannel(canal);
  };
}
